import { type CardTone } from 'partner360/components/card'
import { type TileConfig } from 'partner360/components/tileBoard'
import { PLANNING_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/planningCockpitTourTargets'

import {
  CockpitAttentionStatusesCollapsed,
  CockpitAttentionStatusesExpanded,
  CockpitAttentionStatusesTitle,
} from '../../cockpitAttentionStatuses'
import { AttentionSwitchedOff } from '../../cockpitAttentionStatuses/components/attentionSwitchedOff'
import { CockpitDocumentsAndLinksCollapsed } from '../../cockpitDocumentsAndLinks/CockpitDocumentsAndLinksCollapsed'
import { CockpitDocumentsAndLinksExpanded } from '../../cockpitDocumentsAndLinks/CockpitDocumentsAndLinksExpanded'
import { CockpitDocumentsAndLinksTitle } from '../../cockpitDocumentsAndLinks/components/cockpitDocumentsAndLinksTitle/CockpitDocumentsAndLinksTitle'
import type { TDocumentType } from '../../cockpitDocumentsAndLinks/types'
import { CockpitKeyInitiativesCollapsed } from '../../cockpitKeyInitiatives/CockpitKeyInitiativesCollapsed'
import { CockpitKeyInitiativesExpanded } from '../../cockpitKeyInitiatives/CockpitKeyInitiativesExpanded'
import { CockpitKeyInitiativesTitle } from '../../cockpitKeyInitiatives/CockpitKeyInitiativesTitle'
import { CockpitRevenueAndSobCollapsed } from '../../cockpitRevenueAndSob/CockpitRevenueAndSobCollapsed'
import { CockpitRevenueAndSobExpanded } from '../../cockpitRevenueAndSob/CockpitRevenueAndSobExpanded'
import { CockpitRevenueAndSobTitle } from '../../cockpitRevenueAndSob/components'

/** Named so a tour step's EXPAND_TILE payload cannot drift from the board. */
export const COCKPIT_TILE_IDS = {
  salesTeam: 'salesTeam',
  revenueAndSob: 'revenueAndSob',
  initiatives: 'initiatives',
  attention: 'tile3x2',
  documentsAndLinks: 'documentsAndLinks',
} as const

interface IBuildTilesOptions {
  documentsType?: TDocumentType
  onDocumentsTypeChange?: (type: TDocumentType) => void
  /** The programme in view is switched off for the partner: every tile is greyed, and What needs
   *  attention says its statuses are not calculated. */
  isSwitchedOff?: boolean
  /** The programme has just been switched on and its data fetched again. */
  isRefreshed?: boolean
}

export const buildTiles = (
  partnerId?: string,
  attentionTone?: CardTone,
  {
    isSwitchedOff = false,
    isRefreshed = false,
    documentsType,
    onDocumentsTypeChange,
  }: IBuildTilesOptions = {}
): TileConfig[] => [
  {
    id: COCKPIT_TILE_IDS.revenueAndSob,
    muted: isSwitchedOff,
    highlighted: isRefreshed,
    tourId: TOUR_TARGETS.tileRevenue,
    expandedTourId: TOUR_TARGETS.tileRevenueExpanded,
    title: 'Financial & SOB',
    subtitle: '',
    colSpan: 2,
    rowSpan: 1,
    minHeight: 'min-content',
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitRevenueAndSobTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        withQuota
        viewSwitchTourId={TOUR_TARGETS.revenueViewSwitch}
      />
    ),
    renderCollapsed: ({ expand }) => <CockpitRevenueAndSobCollapsed onExpand={expand} />,
    renderExpanded: () => <CockpitRevenueAndSobExpanded />,
  },
  {
    id: COCKPIT_TILE_IDS.initiatives,
    muted: isSwitchedOff,
    highlighted: isRefreshed,
    tourId: TOUR_TARGETS.tileInitiatives,
    expandedTourId: TOUR_TARGETS.tileInitiativesExpanded,
    title: 'Initiatives',
    subtitle: '',
    colSpan: 2,
    rowSpan: 1,
    minHeight: 'min-content',
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitKeyInitiativesTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        viewSwitchTourId={TOUR_TARGETS.initiativesViewSwitch}
      />
    ),
    renderCollapsed: ({ expand }) => <CockpitKeyInitiativesCollapsed onExpand={expand} />,
    renderExpanded: () => <CockpitKeyInitiativesExpanded />,
  },
  {
    id: COCKPIT_TILE_IDS.attention,
    muted: isSwitchedOff,
    highlighted: isRefreshed,
    tourId: TOUR_TARGETS.tileAttention,
    expandedTourId: TOUR_TARGETS.tileAttentionExpanded,
    title: 'What needs attention',
    subtitle: '',
    /* Greyed, the tile carries no tone of a state it does not calculate. */
    tone: isSwitchedOff ? undefined : attentionTone,
    colSpan: 3,
    rowSpan: 2,
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitAttentionStatusesTitle title={title} expanded={expanded} onToggle={onToggle} />
    ),
    renderCollapsed: ({ expand }) => (
      <AttentionSwitchedOff>
        <CockpitAttentionStatusesCollapsed onExpand={expand} />
      </AttentionSwitchedOff>
    ),
    renderExpanded: ({ collapse }) => (
      <AttentionSwitchedOff>
        <CockpitAttentionStatusesExpanded onCollapse={collapse} />
      </AttentionSwitchedOff>
    ),
  },
  {
    id: COCKPIT_TILE_IDS.documentsAndLinks,
    muted: isSwitchedOff,
    tourId: TOUR_TARGETS.tileDocuments,
    expandedTourId: TOUR_TARGETS.tileDocumentsExpanded,
    title: 'Documents and links',
    subtitle: '',
    colSpan: 1,
    rowSpan: 2,
    hideExpandButton: true,
    renderTitle: ({ title, expanded, onToggle }) => (
      <CockpitDocumentsAndLinksTitle
        title={title}
        expanded={expanded}
        onToggle={onToggle}
        partnerId={partnerId}
      />
    ),
    renderCollapsed: ({ expand }) => (
      <CockpitDocumentsAndLinksCollapsed
        partnerId={partnerId}
        onExpand={expand}
        onViewDocuments={(type) => {
          onDocumentsTypeChange?.(type)
          expand()
        }}
      />
    ),
    renderExpanded: () => (
      <CockpitDocumentsAndLinksExpanded partnerId={partnerId} initialDocumentType={documentsType} />
    ),
  },
]
